import {
  DndContext,
  DragEndEvent,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {restrictToParentElement} from '@dnd-kit/modifiers';
import {SortableContext, arrayMove} from '@dnd-kit/sortable';
import {
  CollectionType,
  Common,
  CoreDoms,
  PageAPI,
  ViewMode,
  EditorExperienceType,
} from '@taopix/taopix-core';
import {PageInterface, ProductLayout} from '@taopix/taopix-core/dist/types/types';
import {
  CloseIcon,
  HelpTip,
  Horizontal,
  Icon,
  InfoIcon,
  LoadingCircleIcon,
  PlusIcon,
  Theme,
  ThemeName,
  Vertical,
} from '@taopix/taopix-design-system';
import classNames from 'classnames';
import React, {
  CSSProperties,
  RefObject,
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {PagesSelector} from '../../../Selectors';
import {useTaopix} from '../../Context/TaopixContext';
import {UserInterfaceActions} from '../../Context/UserInterface/UserInterfaceActionTypes';
import {useUiState} from '../../Context/UserInterface/UserInterfaceContext';
import {useExperience} from '../../Experience/ExperienceProvider';
import AddPagesButton from '../../Page/Actions/AddPagesButton';
import Page from '../../Page/Page';
import PageName from '../../Page/PageName';
import PageToolbar from '../../Page/PageToolbar';
import SortablePage from '../../Sortable/SortablePage';
import {useUserFlowState} from '../../Context/UserFlow/UserFlowContext';
import {useUserFeedback} from '../../Context/UserFeedback/UserFeedbackContext';
import {UserFeedbackActions} from '../../Context/UserFeedback/UserFeedbackActionTypes';
import {PageActionsProvider} from '../../Context/PageActions/PageActionsContext';

export type GridViewProps = {
  layout: ProductLayout;
  containerRef: RefObject<HTMLElement>;
};

const GridView = ({layout}: GridViewProps) => {
  const app = useTaopix();
  const ui = useUiState();
  const {experience} = useExperience();
  const userFeedback = useUserFeedback();
  const {t} = useTranslation();

  const perms = experience.EDITOR.workArea.gridView;

  const [isDragging, setIsDragging] = useState(false);
  const [gridColWidth, setGridColWidth] = useState(ui.state.gridSize);
  const gridTargetColWidth = 360; // Used to calculate a suitable number of cols for current screen and a tile size (gridMaxColWidth) for the core that will fill the available space neatly.
  const gridSmallScreenColCount = 2; // The default column count for small screen
  const gridColGap = 40; // The size of the gap between each column in the grid
  const gridOuterBuffer = ui.state.isSmallScreen ? 40 : 60; // The amount of space to leave on each side of the grid element so it doesn't hit the edge of the workarea
  const gridCellPadding = ui.state.isSmallScreen ? 0 : 10; // The amount of space to add around each page (desirable if there is a hover state on the cell)
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef<boolean>(true);
  const previousIsSmallScreen = useRef(ui.state.isSmallScreen);
  const autoGridSizeSwitching = useRef(ui.state.autoGridSizeSwitching);
  const gridUlRef = useRef<HTMLUListElement>(null);
  const userFlow = useUserFlowState();
  const activeUserFlowStagesCount = userFlow.getActiveStages().length;

  // get the pages from the store
  const pages = useSelector(PagesSelector);

  const [ordering, setOrdering] = useState(
    pages.reduce((acc, curr, i) => {
      if (!acc[curr.pagenumber]) {
        acc[curr.pagenumber] = i;
      }
      return acc;
    }, {})
  );

  const refs = useMemo(() => {
    return pages.map(page => createRef<PageAPI>());
  }, [pages, ordering]); // we also need to regerate the refs when the ordering changes

  // const frontCoverRefIndex = refs.findIndex(ref => {
  //   return Common.isFrontCover(ref.current?.getPageNumber() || 0);
  // });

  // React.useEffect(() => {
  //   if (!ui.state.flags.coverNeedsChecking || 0 === layout.cover.cover1flexiblespine) {
  //     return;
  //   }

  //   if (frontCoverRefIndex > -1 && refs[frontCoverRefIndex] && refs[frontCoverRefIndex].current) {
  //     // Page is loaded and in view.
  //     if (null !== refs[frontCoverRefIndex].current?.getStage()) {
  //       ui.dispatch(UserInterfaceActions.setFlags({coverNeedsChecking: false}));
  //     }
  //   }
  // }, [layout.code, refs[frontCoverRefIndex]?.current]);

  /* Introduce setOrdering for when the pages change otherwise the ordering will not respond to addDelete pages */
  useEffect(() => {
    setOrdering(
      pages.reduce((acc, curr, i) => {
        if (!acc[curr.pagenumber]) {
          acc[curr.pagenumber] = i;
        }
        return acc;
      }, {})
    );
  }, [pages]);

  // sort the pages based on their css sort
  const sortedPages = useMemo(() => {
    return pages.reduce<PageInterface[]>((acc, page) => {
      acc.splice(ordering[page.pagenumber], 0, page);
      return acc;
    }, []);
  }, [pages, ordering]);

  // set up re-arrange sensors
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 3,
      },
    }),
    useSensor(TouchSensor, {
      // Press delay of 250ms, with tolerance of 5px of movement
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    })
  );

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      setIsDragging(false);

      const {active, over} = event;

      if (over?.id && active.id === over?.id) {
        return void 0;
      }

      const from = pages.find(item => item.id === active.id);
      const to = pages.find(item => item.id === over?.id);

      if (!from || !to) {
        return void 0;
      }

      const oldIndex = pages.findIndex(item => item.id === active.id);
      const newIndex = pages.findIndex(item => item.id === over?.id);

      setOrdering(
        arrayMove(pages, oldIndex, newIndex).reduce((acc, curr, i) => {
          if (!acc[curr.pagenumber]) {
            acc[curr.pagenumber] = i;
          }
          return acc;
        }, {})
      );

      app.core
        .get('stateManager')
        .call('movepage', {
          currentPage: ui.state.pageNumber,
          page: from.pagenumber,
          before: to.pagenumber,
        })
        .then(() => {
          setOrdering(
            pages.reduce((acc, curr, i) => {
              if (!acc[curr.pagenumber]) {
                acc[curr.pagenumber] = i;
              }
              return acc;
            }, {})
          );
        })
        .catch(error => {
          const message = error?.message ? error.message : error;
          userFeedback.dispatch(
            UserFeedbackActions.setErrorUserFeedback({
              title: '',
              message,
              showHeader: true,
            })
          );
        });
    },
    [pages]
  );

  const onDragStart = () => {
    ui.dispatch(UserInterfaceActions.setTipOpen({name: 'gridViewIntro', open: false}));
    setIsDragging(true);
    app.core.get('stateManager').deselectAll();
  };

  const showAddDeleteButtons = perms.addDeletePage && layout.minpages !== layout.maxpages;

  // Classes for the UL
  const pagesContainerClasses = classNames(
    'self-start',
    'pt-[80rem]',
    'pb-[250rem]',
    'w-full',

    // Give the grid container padding at either side that matches the column gap to provide space for the floating add pages buttons
    'px-[calc(var(--colGap)*1px)]',

    // If any page is being dragged, add the universal dragging identifier class
    isDragging && 'group drag-active'
  );

  const addPagesContainerClasses = classNames(
    'absolute',
    'top-0',
    'flex',
    'justify-center',

    // Set the size and position of the add pages container to suit to grid size. Must be pixels to match the column width set by the core
    'left-full w-[calc(var(--colGap)*1px)]',

    // Set the height of the add pages container to suit whether the toolbar is present or not
    ui.state.isSmallScreen ? 'h-[calc(100%-30rem)]' : ' h-[calc(100%-60rem)]'
  );

  const pageContainerClasses = classNames(
    'flex',
    'flex-1',
    'justify-center',
    'flex-col',
    'relative',
    'group/grid-tile',
    'rounded-xs',
    'mouse:p-sm', // Padding is to give the page some space within the hover background, so is only required for mouse devices
    '[.draggable_&]:!cursor-grab',
    isDragging ? 'dragging' : '[.draggable_&]:mouse:hover:bg-themeBackgroundColour'
  );

  const pageClassNames = classNames(
    // Show a grab cursor to this and all child canvases
    '[.draggable_&_*]:!cursor-grab',
    // Add styling to the item when it is being dragged
    'group-[.dragging]:shadow-[0_10px_10px_rgb(0_0_0_/_10%)]',
    'group-[.dragging]:rounded-xs',
    'group-[.dragging]:overflow-hidden',
    'group-[.dragging]:outline',
    'group-[.dragging]:outline-[3rem]',
    'group-[.dragging]:outline-offset-[-3rem]',
    'group-[.dragging]:outline-themeAccentColour',
    'group-[.dragging]:translate-y-[-10rem]',
    'group-[.dragging]:!cursor-grabbing'
  );

  const tpxCoreAttrs = {
    [CoreDoms.ProjectPagesAttr]: 'true',
  };

  const editorType = useMemo(() => {
    return ui.state.canEditPageLayout ? EditorExperienceType.Grid : EditorExperienceType.Locked;
  }, [ui.state.canEditPageLayout]);

  useEffect(() => {
    // Show the 'rearrange pages' tip when in grid view, but only if the toolpalette intro tip is not visible
    if (experience.EDITOR.workArea.gridView.canRearrangePages) {
      const timeout = window.setTimeout(() => {
        if (
          ui.state.viewMode === ViewMode.Grid &&
          !ui.state.tips.toolPaletteIntro &&
          activeUserFlowStagesCount === 1
        ) {
          ui.dispatch(UserInterfaceActions.setTipOpen({name: 'gridViewIntro', open: true}));
        }
      }, 1000);

      return () => {
        if (timeout) {
          window.clearTimeout(timeout);
        }
      };
    }
  }, [ui.state.viewMode, ui.state.tips.toolPaletteIntro]);

  const updateGridSize = () => {
    if (gridContainerRef.current) {
      // Calculate the required grid column width by dividing the available width (minus cell padding, column gaps and the outer buffer) by the column count
      const gridContainerWidth = gridContainerRef.current?.clientWidth;
      const gridDefaultColCount = ui.state.isSmallScreen
        ? gridSmallScreenColCount
        : Math.round(gridContainerWidth / gridTargetColWidth);
      const availableContainerWidth = gridContainerWidth - gridOuterBuffer * 2;
      const cellExtras = gridCellPadding * 2 * gridDefaultColCount;
      const colGaps = gridColGap * (gridDefaultColCount - 1);
      const newGridColWidth =
        (availableContainerWidth - cellExtras - colGaps) / gridDefaultColCount;
      // Update the grid size in the UI context
      ui.dispatch(UserInterfaceActions.setGridSize(newGridColWidth));
    }
  };

  useEffect(() => {
    isFirstRender.current = false;

    if (ui.state.isSmallScreen) {
      // If we're on small screen, reset to auto grid size switching
      // and store the auto switching sate in a local ref so we have access to it immediately
      ui.dispatch(UserInterfaceActions.setAutoGridSizeSwitching(true));
      autoGridSizeSwitching.current = true;
    }

    if (autoGridSizeSwitching.current) {
      updateGridSize();
    }
  }, []);

  useEffect(() => {
    if (!isFirstRender.current && previousIsSmallScreen.current !== ui.state.isSmallScreen) {
      // When changing screen size (but not on the first render), update the grid size after a delay (to help avoid updating while still resizing)
      // and store the changed isSmallScreen state in a ref to allow future comparison
      setTimeout(updateGridSize, 1000);
      previousIsSmallScreen.current = ui.state.isSmallScreen;
    }
  }, [ui.state.isSmallScreen]);

  // Update the grid size state whenever the context is changed by updateGridSize or zoom controls
  useEffect(() => {
    if (ui.state.gridSize !== null && ui.state.gridSize !== gridColWidth) {
      setGridColWidth(ui.state.gridSize);
    }
  }, [ui.state.gridSize]);

  // Update the auto grid sizing ref when the context changes (i.e. when the zoom buttons are used)
  useEffect(() => {
    autoGridSizeSwitching.current = ui.state.autoGridSizeSwitching;
  }, [ui.state.autoGridSizeSwitching]);

  useEffect(() => {
    ui.dispatch(
      UserInterfaceActions.setGridViewData({...ui.state.gridViewColumnData, sortedPages})
    );
  }, [sortedPages]);

  useEffect(() => {
    setTimeout(() => {
      if (gridUlRef.current) {
        const gridTemplateColumns = window
          .getComputedStyle(gridUlRef.current)
          .getPropertyValue('grid-template-columns');
        const columns = gridTemplateColumns.split(' ').length;
        ui.dispatch(
          UserInterfaceActions.setGridViewData({...ui.state.gridViewColumnData, columns})
        );
      }
    });
  }, [gridUlRef.current, ui.state.gridSize]);

  return (
    <div
      id={'gridContainer'}
      className={'flex flex-1 justify-center items-center w-full h-full'}
      ref={gridContainerRef}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToParentElement]}
        onDragEnd={onDragEnd}
        onDragStart={onDragStart}
      >
        <SortableContext items={sortedPages}>
          <>
            {gridColWidth === null ? (
              <Icon icon={<LoadingCircleIcon />} size={'xlarge'} className={'mt-[80rem]'} />
            ) : (
              <ul
                {...tpxCoreAttrs}
                className={pagesContainerClasses}
                key={editorType}
                style={
                  {
                    '--colGap': showAddDeleteButtons ? gridColGap : 0,
                  } as CSSProperties
                }
                ref={gridUlRef}
              >
                {sortedPages.map((page: PageInterface, index: number) => {
                  return (
                    <SortablePage
                      page={page}
                      key={`gridview-page-${page.ref}`}
                      styles={{order: ordering[page.pagenumber]}}
                      className={classNames(ui.state.pageNumber === page.pagenumber && 'selected')}
                    >
                      <Theme
                        name={ThemeName.Container}
                        allowBackground={false}
                        allowBorder={false}
                        allowCorners={false}
                        className={pageContainerClasses}
                      >
                        <Page
                          page={page}
                          layout={layout}
                          editorType={editorType}
                          viewMode={ViewMode.Grid}
                          gridCellMaxWidth={gridColWidth}
                          gridCellMaxHeight={gridColWidth * 1.1}
                          gridColGap={showAddDeleteButtons ? gridColGap : undefined}
                          gridRowGap={20}
                          gridCellPadding={ui.state.isSmallScreen ? 0 : 10}
                          showErrors={true}
                          showEmptyBoxes={true}
                          className={pageClassNames}
                          ref={refs[index]}
                        >
                          <>
                            <PageName page={page} layout={layout} />
                            <PageActionsProvider>
                              <PageToolbar pageNumber={page.pagenumber} visible={!isDragging} />
                            </PageActionsProvider>
                          </>
                        </Page>

                        {showAddDeleteButtons && !isDragging && (
                          <div className={addPagesContainerClasses}>
                            {
                              // Prevent the add pages button appearing between the cover and inside left
                              !Common.isBackCover(page.pagenumber) &&
                                (!Common.isPageCover(page.pagenumber) ||
                                  1 !== layout.firstpagenumber) &&
                                layout.minpages !== layout.maxpages && (
                                  <AddPagesButton
                                    buttonProps={{
                                      buttonStyle: 'tertiary',
                                      size: ui.state.isSmallScreen ? 'xsmall' : 'small',
                                      hideLabel: true,
                                      corners: 'round',
                                      className: 'self-center',
                                      startIcon: <PlusIcon />,
                                    }}
                                    pageNumber={page.pagenumber}
                                  />
                                )
                            }
                          </div>
                        )}
                      </Theme>
                    </SortablePage>
                  );
                })}
              </ul>
            )}
          </>
        </SortableContext>
      </DndContext>
      <HelpTip
        id="gridViewHelpTip"
        open={ui.state.tips.gridViewIntro}
        message={
          ui.state.isSmallScreen ? t('*:str_LabelTapAndHoldPages') : t('*:str_LabelDragPages')
        }
        anchorId={'viewerToolbar'}
        anchorOrigin={
          ui.state.isSmallScreen
            ? {vertical: Vertical.Bottom, horizontal: Horizontal.Centre}
            : {vertical: Vertical.Bottom, horizontal: Horizontal.Left}
        }
        transformOrigin={
          ui.state.isSmallScreen
            ? {vertical: Vertical.Top, horizontal: Horizontal.Centre}
            : {vertical: Vertical.Top, horizontal: Horizontal.Left}
        }
        offset={ui.state.isSmallScreen ? undefined : {x: 10, y: 0}}
        className={'max-w-[min(400rem,theme(width.bounds))]'}
        buttonFunction={() =>
          ui.dispatch(UserInterfaceActions.setTipOpen({name: 'gridViewIntro', open: false}))
        }
        icon={<InfoIcon />}
        buttonProps={{startIcon: <CloseIcon />, hideLabel: true}}
        repositionOnResize
        showAfter={500}
        showMode="once"
      />
    </div>
  );
};

export default GridView;
